/*****************************************************
 * Packages
 ******************************************************/
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, Redirect } from 'react-router-dom';
import Frame from 'react-frame-component';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import ReactGA from 'react-ga';
import React from 'react';

/*****************************************************
 * Locals
 ******************************************************/
import AddSectionModal from '../setting-components/Modals/AddSectionModal';
import configIntegration from '../../editor-resources/config-integration';
import elementSettings from '../../editor-resources/settings-priority';
import globalSettings from '../../editor-resources/global-settings';
import EditorModal from '../setting-components/Modals/EditorModal';
import { BuilderWrap, BuilderBody } from './BuilderAside.sc';
import FrameBindingContext from '../FrameBindingContext';
import dataFromAddress from '../../util/dataFromAddress';
import CreateSection from '../../util/CreateSection';
import CreateColumn from '../../util/CreateColumn';
import RecursiveRender from '../RecursiveRender';
import assignNewId from '../../util/assignNewId';
import CreateRow from '../../util/CreateRow';
import { themes } from '../../ThemeContext';
import BuilderAside from './BuilderAside';
import deepCopy from '../../util/deepCopy';
import NavItemMore from './NavItemMore';
import { API_URL } from '../../config';
import Dropdown from '../Dropdown';
import {
    EditorContextProvider,
    ElementContextProvider,
} from '../../ElementRenderContext';
import BuilderHeader, {
    NavUl,
    HeaderNav,
} from '../other-components/BuilderHeader';
import * as actions from '../../redux/actions';
import { globalStyles } from '@getanwar/style-generator';
import buildFontString from '../../util/buildFontString';
import PublishSiteModal from '../setting-components/Modals/PublishSiteModal';
import SaveElementModal from '../ElementControls/SaveElementModal';
import initialData from './initialData';
import {
    getSite,
    getAuthUser,
    getTemplate,
    getIsSavedSite,
} from '../../redux/selectors';
import Image from '../Image';
import debounce from 'lodash.debounce';
import { getPage } from '../../redux/selectors/site';
import siteSettings from '../../editor-resources/site-settings';
import uuid from '../../util/uniqId';

class Builder extends React.Component {
    state = {
        responsiveEditorMode: 'desktop',
        settingsWindowVisible: false,
        currentEditItem: null,
        theme: themes.light,
        settingsModal: '',
        editAddress: null,
        addElType: '',
        data: initialData,
        isLoadingBuilder: true,
        isPublishing: false,
        isSaving: false,
        shouldRecordHistory: false,
        editPage: null,
        editPageIndex: 0,
        pages: initialData.pages.slice(),
        global: { ...initialData.global },
        hasModifiedGlobal: true,
        activeSidebar: null,
        _history: {
            cursor: -1,
            records: [],
        },
        history: {
            past: [],
            future: [],
        },
    };

    toggleActiveSidebar = (activeSidebar) => {
        activeSidebar === this.state.activeSidebar
            ? this.setState({ activeSidebar: null })
            : this.setState({ activeSidebar });
    };

    /**
     * @typedef PageObject
     * @property {Object} [data]
     * @property {String} [name]
     *
     * @returns {PageObject}
     */
    getEditPage = () => {
        const { pages, editPageIndex } = this.state;
        return pages[editPageIndex] || {};
    };

    getEditPageById = (pageId) => {
        const { pages } = this.state;
        return pages.find((page) => page.id === pageId);
    };

    getEditPageIndexById = (pageId) => {
        const { pages } = this.state;
        return pages.findIndex((page) => page.id === pageId);
    };

    /**
     * @typedef {Object} PageObject
     * @property {Object} [data]
     * @property {String} [name]
     *
     * @param {PageObject} item the page item
     * @param {number} [editPageIndex] index of the page to be edited, defualt `from state`
     * @param {boolean} [isModified] to flag the item as modified, default `true`
     * @param {function} [callback] callback to call after setState
     */
    setEditPage = (item, editPageIndex, isModified = true, callback) => {
        if (typeof editPageIndex === 'function') {
            callback = editPageIndex;
            editPageIndex = undefined;
        }
        editPageIndex =
            undefined != editPageIndex
                ? editPageIndex
                : this.state.editPageIndex;

        const { pages } = this.state;
        const nextPage = { ...pages[editPageIndex], ...item, isModified };
        this.setState(
            {
                editPageIndex,
                pages: pages
                    .slice(0, editPageIndex)
                    .concat(nextPage)
                    .concat(pages.slice(editPageIndex + 1)),
            },
            callback
        );
    };

    addToEditPages = (page) => {
        const { pages } = this.state;
        if (!page.data) page.data = { content: [] };
        const nextPages = pages.concat({ ...page, isModified: true });
        this.setState({ pages: nextPages });
    };

    duplicatePage = (index) => {
        const { pages } = this.state;
        const page = pages[index];
        const copyPage = {
            ...page,
            slug: '',
            type: null,
            isModified: true,
            name: `${page.name} Copy`,
        };
        delete copyPage.id;
        const nextPages = [...pages];
        nextPages.splice(nextPages.length, 0, copyPage);
        this.setState({ pages: nextPages });
    };

    removeEditPage = (index) => {
        const { pages, editPageIndex } = this.state;
        if (index === editPageIndex) {
            this.setState({ editPageIndex: 0 });
        }

        const nextPages = [...pages];
        nextPages.splice(index, 1);
        this.setState({
            pages: nextPages,
        });
    };

    /**
     * @typedef {Object} Page
     * @property {String} id
     * @property {String} name
     * @property {String} slug
     *
     * @param {Page} page
     * @param {Number} index
     */
    handleSelectPage = (index) => {
        const page = this.state.pages[index];
        const { location } = this.props;
        this.setState({ editPageIndex: index });
        if (location.pathname.includes('page') && !page.id) {
            this.setState({ data: initialData });
        }
    };

    clonePageData = (pageId) => {};

    fetchData = async ([queryName = 'site', id]) => {
        this.setEditingPage();
        const siteQuery = {
            variables: { id },
            query: `query fetchData($id: ID!) {
                ${queryName}(id: $id) {
                    data
                }
            }`,
        };
        const response = await fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(siteQuery),
        });
        const result = await response.json();
        const data = JSON.parse(result.data[queryName].data);
        if (data) {
            this.setState({ data });
        } else {
            console.warn("Couldn't load data");
        }
    };

    parseURLId = (query) => {
        const params = new URLSearchParams(query);
        if (params.has('e')) {
            return ['site', params.get('e')];
        } else if (params.has('t')) {
            return ['template', params.get('t')];
        }
    };

    handleCloseTab = (e) => {
        const { isSaved } = this.props;
        if (!isSaved) {
            // Cancel the event
            e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
            // Chrome requires returnValue to be set
            e.returnValue = '';
        } else {
            delete e['returnValue'];
        }
    };

    findHomePage = () => {
        const { site } = this.props;
        return site?.pages?.find((page) => page.type === 'INDEX');
    };

    syncPages = (cb) => {
        const {
            site: { pages = [] },
        } = this.props;
        const { pages: statePages } = this.state;

        const results = pages.map((page) => {
            const statePage = statePages.find((item) => item.id === page.id);
            return {
                data: { content: [] },
                ...page,
                ...statePage,
                isModified: false,
            };
        });
        this.setState({ pages: results }, cb);
    };

    componentDidMount() {
        const { siteId, templateId, dispatch, pageId } = this.props;
        if (siteId) {
            dispatch(actions.fetchSite(siteId, pageId));
        } else if (templateId) {
            dispatch(actions.getTemplate(templateId));
        }

        if (!siteId) {
            setTimeout(() => {
                this.setState({ isLoadingBuilder: false });
            }, 1500);
        }
    }

    shouldRequestPage = (pageId) => {
        const page = this.getEditPageById(pageId);
        if (!page) return true;
        return !page.isModified;
    };

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleCloseTab);
    }

    componentDidUpdate(prevProps, prevState) {
        const { site, siteId, isSaved, pageId, page, dispatch } = this.props;

        // fetch new page when route changes
        if (pageId && pageId !== prevProps.pageId) {
            this.setState({
                isLoadingBuilder: true,
                _history: { records: [], cursor: -1 },
            });

            const yes = this.shouldRequestPage(pageId);
            yes && dispatch(actions.fetchPage(pageId));
        }

        // Stop preloader 300ms after page switch
        if (page && page !== prevProps.page) {
            setTimeout(() => {
                this.setState({ isLoadingBuilder: false });
            }, 300);
        }

        // When server returns a site with all pages
        if (site?.pages !== prevProps.site?.pages) {
            this.syncPages(() => {
                const activePageIndex = this.getEditPageIndexById(pageId);
                this.setState({ editPageIndex: activePageIndex });
            });
        }

        if (site !== prevProps.site) {
            this.setState({ isSaving: false, isPublishing: false });
            site?.global && this.setState({ global: JSON.parse(site.global) });
            if (site?.data) {
                const data = JSON.parse(site.data);
                const { content, ...rest } = data;
                this.setState({
                    global: rest,
                    hasModifiedGlobal: true,
                    isLoadingBuilder: false,
                });
                this.setEditPage(
                    { data },
                    () => siteId && this.handleSaveData(false)
                );
                ReactGA.event({
                    category: 'Migration',
                    action: 'Save Homepage',
                    label: 'Migrate old sitedata to home',
                });
            }
        }

        // When server returns a page
        if (page && page !== prevProps.page) {
            const pageIndex = this.getEditPageIndexById(page.id);
            const { pages } = this.state;
            const currentPage = pages[pageIndex];
            this.setState({ editPageIndex: pageIndex });
            if (pageIndex >= 0 && !currentPage.isModified && page.data) {
                this.setEditPage(
                    { data: JSON.parse(page.data) },
                    pageIndex,
                    false
                );
            }
        }

        if (prevProps.isSaved !== isSaved) {
            if (isSaved) {
                window.removeEventListener('beforeunload', this.handleCloseTab);
            } else {
                window.addEventListener('beforeunload', this.handleCloseTab);
            }
        }
    }

    /**
     * Temporarily store data in localStorage
     * Need more improvement
     * @param {object} data
     */
    storeState(data) {
        data = JSON.stringify(data);
        if (data) {
            localStorage.setItem('siteData', data);
        }
    }

    makeCurrentEditItem = (data, address) => {
        address = address ? address.split('.') : [];
        const currentEditItem = address.reduce(
            (acc, index) => acc.content[index],
            data
        );
        this.setState({ currentEditItem });
    };

    savedHistory = {
        items: new Map(),
        pointer: null,
    };

    pushToHistory = (id, data) => {
        this.savedHistory.items.set(id, data);
        this.savedHistory.pointer = id;
    };

    browseSavedHistory = (position) => {
        const { items, pointer } = this.savedHistory;
        const history = Array.from(items.keys());
        const currentIndex = history.indexOf(pointer) + position;
        const currPoi = history[currentIndex];
        this.savedHistory.pointer = currPoi;

        this.setState({ data: items.get(currPoi) });
    };

    onDragItem = (props) => {
        this.pendingUpdateFn = props;
        if (!this.requestedFrame) {
            this.requestedFrame = requestAnimationFrame(this.drawFrame);
        }
    };
    drawFrame = () => {
        const page = this.getEditPage();
        const props = this.pendingUpdateFn;
        const [dataWithReference, sourceItem] = this.shallowData(
            page.data,
            props.source.parent
        );
        const source = sourceItem.content.slice();
        const [dragItem] = source.splice(props.source.index, 1);
        sourceItem.content = source;

        const [data, destinationItem] = this.shallowData(
            dataWithReference,
            props.destination.parent
        );
        const newDest = destinationItem.content.slice();
        newDest.splice(props.destination.index, 0, dragItem);
        destinationItem.content = newDest;
        this.saveToHistory({ data: page.data });
        this.setEditPage({ data });
        this.pendingUpdateFn = undefined;
        this.requestedFrame = undefined;
    };

    /**
     * Removes element from the data store
     * @param {string} address
     * @returns void
     */
    removeElement = (address) => {
        const page = this.getEditPage();
        const newAddress = address.split('.');
        const removeIndex = newAddress.pop();
        const [data, editItem] = dataFromAddress(
            page.data,
            newAddress.join('.'),
            true
        );
        editItem.content.splice(removeIndex, 1);
        this.saveToHistory({ data: page.data });
        this.setEditPage({ data });
    };

    /**
     * Duplicate an element
     * @param {string} address
     * @returns void
     */
    duplicateElement = (address) => {
        const { dispatch } = this.props;
        const page = this.getEditPage();
        const copyItem = dataFromAddress(page.data, address, true)[1];
        assignNewId(copyItem);

        const newAddress = address.split('.');
        const copyIndex = newAddress.pop();
        // TODO: Should not deep copy
        const [data, editItem] = dataFromAddress(
            page.data,
            newAddress.join('.'),
            true
        );

        editItem.content.splice(Number(copyIndex) + 1, 0, copyItem);
        this.saveToHistory({ data: page.data });
        this.setEditPage({ data }, null, true, () => {
            dispatch(actions.setSavedStatus(false));
        });
    };

    // TODO: need to refactor / improve
    createColumn = (num) => {
        const col = new CreateColumn();
        const page = this.getEditPage();
        const data = JSON.parse(JSON.stringify(page.data));
        const editItem = this.state.editAddress
            .split('.')
            .reduce((acc, index) => acc.content[index], data);
        if (num > editItem.content.length) {
            editItem.content.push(col);
        }
        this.saveToHistory({ data: page.data });
        this.setState({ data });
    };

    /**
     * Kept the code just for future referance
     * need performance improvement
     * and final implementation before final release
     */
    updateSpacing = (address, name, direction, newData) => {
        const styleMap = new Map(this.state.data.content[0].style[name]);
        styleMap.set(direction, newData);
        const data = JSON.parse(JSON.stringify(this.state.data));
        data.content[0].style[name] = Array.from(styleMap);
        this.setState({ data });
    };

    /**
     * This method determines where to insert the Column
     * @param {Number} cols
     * @param {String} contentOf
     * @returns void
     */
    addColumn = (cols) => {
        const { addElType } = this.state;
        const columns = cols.split('+');
        const colCount = columns.reduce((num, curr) => num + parseInt(curr), 0);

        if (addElType === 'ROW') {
            const row = new CreateRow('Row', colCount);
            row.content = columns.map(
                (span) =>
                    new CreateColumn({
                        size: `${span}/${colCount}`,
                    })
            );
            this.insertElement(row);
        } else if (addElType === 'COLUMN') {
            const column = new CreateColumn({
                size: `${columns[0]}/${colCount}`,
            });
            this.insertElement(column);
        } else if (addElType === 'SECTION') {
            const section = new CreateSection();
            const row = new CreateRow();
            row.content = columns.map(
                (span) =>
                    new CreateColumn({
                        size: `${span}/${colCount}`,
                    })
            );
            section.content.push(row);
            this.insertElement(section);
        }
    };

    addElement = ({ data, elType }) => {
        const { addElType } = this.state;
        const elData = deepCopy(data);
        assignNewId(elData);
        if (addElType === 'SECTION') {
            if (elType === 'SECTION') {
                this.insertElement(elData);
            } else if (elType === 'ROW') {
                const section = new CreateSection();
                section.content.push(elData);
                this.insertElement(section);
            }
        } else if (addElType === 'ROW') {
            if (elType === 'ROW') {
                this.insertElement(elData);
            }
        } else if (addElType === 'ELEMENT') {
            if (elType === 'ELEMENT') {
                this.insertElement(elData);
            }
        } else if (elType === 'COLUMN' && addElType === 'COLUMN') {
            this.insertElement(elData);
        }
    };

    insertElement = async (elData, address = this.state.editAddress) => {
        // const { data } = this.state;
        const page = this.getEditPage();
        const data = page.data;
        const { dispatch } = this.props;
        const addr = address ? address.split('.') : [];
        const destination = addr.length ? parseInt(addr.pop()) + 1 : 0;
        const [siteData, source] = this.shallowData(data, addr.join('.'));
        const newContent = source.content.slice();
        newContent.splice(destination, 0, elData);
        source.content = newContent;
        this.saveToHistory({ data: page.data });
        this.setEditPage({ data: siteData });
        // this.setState({ data: siteData });
        this.hideAddModal(null, triggerEditorModal);
        // Site saved status update
        dispatch(actions.setSavedStatus(false));

        // Show Editor Modal
        function triggerEditorModal() {
            elData._elType === 'ELEMENT' &&
                this.handleClickSettings(`${addr.join('.')}.${destination}`);
        }
    };

    /**
     * Determins which modal to show
     * @param {string} contentOf
     * @param {string} address A concatanated indexes of data contents
     */
    showAddModal = (addElType, address) => {
        this.setState({ addElType, editAddress: address });
    };

    hideAddModal = (event, fn) => {
        this.setState({ editAddress: null, addElType: '' }, fn);
    };
    /**
     * Called when clicked on settings control button
     * It shows settings modal
     * @param {string} address - A concatanated indexes of data contents
     */
    handleClickSettings = (address) => {
        this.showSettingsModal('COMPONENT_SETTINGS', address);
    };

    showSettingsModal = (type, address) => {
        this.props.dispatch({ type: 'settingsWindowVisible' });
        this.setState({
            settingsModal: type,
            editAddress: address,
        });

        if (type === 'GLOBAL-SETTINGS' || type === 'SITE-SETTINGS') {
            // TODO: enable it on first input change instead of openning the settings
            this.setState({
                hasModifiedGlobal: true,
            });
            this.makeCurrentEditItem(this.state.global);
        } else {
            const data = this.getDataByAddress(address);
            this.makeCurrentEditItem(data);
        }
    };

    hideSettingsModal = () => {
        this.props.dispatch({ type: 'hideSettingsWindow' });

        this.setState({
            editAddress: null,
            settingsModal: null,
            currentEditItem: null,
        });
    };

    /**
     * To hide settings modal
     */
    hideSettingsWindow = () => {
        this.props.dispatch({ type: 'hideSettingsWindow' });
        this.setState({
            // responsiveEditorMode: "desktop",
            settingsWindowVisible: false,
            currentEditItem: null,
            editAddress: null,
        });
    };

    /**
     * @param {object} data
     * @param {string} address
     * @returns {array}
     */
    shallowData = (data, address) => {
        const addr = address ? address.split('.') : [];
        const result = { ...data };
        const edgeItem = addr.reduce((acc, index) => {
            const arr = [...acc.content]; // shallow copy array
            const nItem = { ...arr[index] }; // shallow copy the item
            arr[index] = nItem; // replace array index with the shallow copied item
            acc.content = arr;
            return nItem;
        }, result);
        return [result, edgeItem];
    };

    getDataByAddress = (address) => {
        const page = this.getEditPage();
        const addr = address ? address.split('.') : [];
        return addr.reduce((acc, index) => acc.content[index], page.data);
    };

    _saveOne = (edgeItem, payload) => {
        const { name: path, value } = payload;
        // This code bellow is written for mutating the currently
        // editable item which is shallow copy of original object
        path.split('/').reduce((acc, key, index, arr) => {
            if (arr.length === index + 1) {
                // assign the value to the last part of the path
                return (acc[key] = value);
            } else {
                // create a blank object, if unvailable
                if (!acc[key]) {
                    acc[key] = {};
                }
                return (acc[key] = { ...acc[key] }); // return without referance
            }
        }, edgeItem);
    };

    /**
     * Triggered when user changes something in settings modal
     * and clicks on Save changes
     * @param {object|array} payload
     * @param {string} [address]
     * @returns void
     */
    handleSaveSettings = (payload, address = this.state.editAddress) => {
        const { currentEditItem, shouldRecordHistory } = this.state;
        const { dispatch } = this.props;
        const page = this.getEditPage();

        // This code bellow is written to make
        // the current editable item immutable
        const [result, edgeItem] = this.shallowData(page.data, address);

        if (Array.isArray(payload)) {
            payload.forEach((load) => this._saveOne(edgeItem, load));
        } else {
            this._saveOne(edgeItem, payload);
        }

        // console.log(
        //     'by referance: ',
        //     result.content[0] === this.state.data.content[0]
        // );

        if (!shouldRecordHistory) {
            this.setState({ shouldRecordHistory: true });
            this.saveToHistory({ data: page.data });
        }

        this.setEditPage({ data: result });

        this.setState({
            data: result,
            currentEditItem: address === null ? currentEditItem : edgeItem,
        });

        // Site saved status update
        dispatch(actions.setSavedStatus(false));

        // Auto save
        this.backgroundSave();
    };

    handleGlobalChanges = (payload) => {
        const { global } = this.state;
        const copyGlobal = { ...global };
        if (Array.isArray(payload)) {
            payload.forEach((load) => this._saveOne(global, load));
        } else {
            this._saveOne(global, payload);
        }

        const record = { data: copyGlobal, type: 'GLOBAL' };
        this.saveToHistory(record);

        this.setState({
            global,
            currentEditItem: global,
        });
    };

    backgroundSave = debounce(() => {
        // const { pages } = this.state;
        // const { authUser } = this.props;
        this.handleSaveData(false);

        // if (!authUser) {
        //     localStorage.setItem('pages-temp', JSON.stringify(pages));
        // }
    }, 3000);

    saveToHistory = debounce((record) => {
        const { editPageIndex, _history, global } = this.state;
        // TODO:
        // fix: other pages histories flash when somehting changed after undo
        let records = _history.records.slice(_history.cursor);
        record = { type: 'PAGE', id: uuid(), ...record };
        if (record.type === 'PAGE' && null == record.index) {
            record.index = editPageIndex;
        }

        // TODO:
        // fix: duplicate record saved when record type is different from prev record
        const [current] = records;
        if (
            current &&
            current.type === record.type &&
            current.index === record.index
        ) {
            records = records.slice(1);
        }
        const newRecords = [record, ...records];
        if (record.type === 'PAGE') {
            const page = this.getEditPage();
            const recent = {
                id: uuid(),
                type: 'PAGE',
                index: editPageIndex,
                data: { ...page.data },
            };
            newRecords.unshift(recent);
        } else if (record.type === 'GLOBAL') {
            const recent = {
                id: uuid(),
                type: 'GLOBAL',
                data: { ...global },
            };
            newRecords.unshift(recent);
        }

        this.setState({
            shouldRecordHistory: false,
            _history: {
                ..._history,
                cursor: 0,
                records: newRecords,
            },
        });
    }, 500);

    updateHistory = (nextRecord) => {
        const { _history, editAddress } = this.state;
        const { records } = _history;
        const { id, type, data } = nextRecord;
        const curCor = records.findIndex((item) => item.id === id);
        if (type === 'PAGE') {
            this.setEditPage({ data });
            editAddress && this.makeCurrentEditItem(data, editAddress);
        } else if (type === 'GLOBAL') {
            this.setState({ global: data });
            !editAddress && this.makeCurrentEditItem(data);
        }
        this.setState({
            _history: { ..._history, cursor: curCor },
        });
    };

    handleUndo = () => {
        const { _history, editPageIndex } = this.state;
        const { records, cursor } = _history;
        const nextCur = cursor + 1;
        const scopedRecords = records
            .slice(nextCur)
            .filter(
                (item) =>
                    item.type === 'GLOBAL' ||
                    (item.type === 'PAGE' && item.index === editPageIndex)
            );
        const [nextRecord] = scopedRecords;
        if (nextRecord) {
            this.updateHistory(nextRecord);
        }
    };

    handleRedo = () => {
        const { _history, editPageIndex } = this.state;
        const { records, cursor } = _history;
        const scopedRecords = records
            .slice(0, cursor)
            .filter(
                (item) =>
                    item.type === 'GLOBAL' ||
                    (item.type === 'PAGE' && item.index === editPageIndex)
            )
            .reverse();
        const [nextRecord] = scopedRecords;
        if (nextRecord) {
            this.updateHistory(nextRecord);
        }
    };

    //TODO: refactor handle save settings
    REF_handleSaveSettings = (payload, address) => {
        const { data, editAddress, currentEditItem } = this.state;
        address = address || editAddress;

        const addr = address ? address.split('.') : [];
        if (currentEditItem) {
            const copyOfCurrentEditItem = { ...currentEditItem };
            const { name, value } = payload;
            // This code bellow is written for mutating the currently
            // editable item which is shallow copy of original object
            const path = name.split('/'),
                len = path.length;
            path.reduce((acc, key, index) => {
                if (len === index + 1) {
                    // assign the value to the last part of the path
                    return (acc[key] = value);
                } else {
                    // create a blank object, if unvailable
                    if (!acc[key]) {
                        return (acc[key] = {});
                    }
                    return (acc[key] = { ...acc[key] });
                }
            }, copyOfCurrentEditItem);
            // TODO: save item into data
        } else {
            const currentEditItem = addr.reduce((acc, index) => {
                const arr = [...acc.content]; // shallow copy contents array
                return { ...arr[index] }; // shallow copy the element
            }, data);
            // TODO: save item into data
            this.setState({ currentEditItem });
        }
    };

    handleImport = (data) => {
        this.setState({ data });
    };

    handleResponsiveEditorMode = (value) =>
        this.setState({ responsiveEditorMode: value });

    setGlobalState = (newGlobalState) => {
        const { global } = this.state;
        this.setState({
            hasModifiedGlobal: true,
            global: {
                ...global,
                ...newGlobalState,
            },
        });
    };

    // to avoid this object to be created each render
    elementContextMethods = {
        handleResponsiveEditorMode: this.handleResponsiveEditorMode,
        responsiveEditorMode: this.state.responsiveEditorMode,
        showSettingsModal: this.showSettingsModal,
        onClickSettings: this.handleClickSettings,
        getDataByAddress: this.getDataByAddress,
        duplicateElement: this.duplicateElement,
        onSaveSettings: this.handleSaveSettings,
        setGlobalState: this.setGlobalState,
        showAddModal: this.showAddModal,
        onRemove: this.removeElement,
        addElement: this.addElement,
        addColumn: this.addColumn,
    };

    get stateToContext() {
        const { global, currentEditItem, responsiveEditorMode } = this.state;
        return {
            global,
            display: responsiveEditorMode,
            currentEditItem: currentEditItem,
        };
    }

    get getFrameStyle() {
        const devices = {
            desktop: '100%',
            tablet: '768px',
            mobile: '420px',
        };
        return {
            border: '0px',
            display: 'block',
            margin: '0 auto',
            background: '#ffffff',
            height: 'calc(100vh - 80px)',
            boxShadow: '0 0 60px 0 rgba(43,53,86,0.15)',
            width: devices[this.state.responsiveEditorMode],
        };
    }

    handleClickOnPublish = () => {
        const { site } = this.props;
        if (!site || !site.status || site.status === 'DRAFT') {
            this.setState({ settingsModal: 'PUBLISH-SITE' });
        } else {
            this.handlePublishSave();
        }
    };

    handlePublishSave = (input = {}) => {
        const { isPublishing, global, hasModifiedGlobal } = this.state;
        if (isPublishing) return;
        const { siteId, pageId, dispatch, history, authUser } = this.props;
        if (!authUser) return;
        this.setState({ isPublishing: true });
        const pages = this.getModifiedPages();
        const data = { global: hasModifiedGlobal ? global : null };

        dispatch(
            actions.saveSite({
                ...input,
                pages,
                history,
                id: siteId,
                publish: true,
                currentPageId: pageId,
                data: JSON.stringify(data),
            })
        );
    };

    getModifiedPages = () => {
        const { pages } = this.state;
        return pages
            .filter((page) => page.isModified)
            .map((page) => {
                page = { ...page };
                if (null === page.type) delete page.type;
                delete page.isModified;
                return {
                    ...page,
                    data: page.data && JSON.stringify(page.data),
                };
            });
    };

    handleSaveData = (isNotify = true) => {
        // TODO: set hasModifiedGlobal = flase after successful save
        const { isSaving, global, hasModifiedGlobal } = this.state;
        const pages = this.getModifiedPages();

        if (isSaving) return;
        const { dispatch, siteId, pageId, history, authUser } = this.props;
        if (!authUser) return;
        this.setState({ isSaving: true });

        const data = { global: hasModifiedGlobal ? global : null };

        dispatch(
            actions.saveSite({
                pages,
                history,
                isNotify,
                id: siteId,
                currentPageId: pageId,
                data: JSON.stringify(data),
            })
        );
    };

    previewSite = () => {
        const { siteId, pageId, isSaved } = this.props;
        if (!isSaved || !siteId) {
            window.alert('Please save changes to preview');
        } else {
            window.open(`/preview/site/${siteId}/page/${pageId}`, '_blank');
        }
    };

    handleExportJSON = () => {
        const { dispatch, siteId } = this.props;
        dispatch(actions.exportJSON(siteId));
    };

    importJSON = ({ pages, global }) => {
        const importedPages = pages.map((page) => ({
            ...page,
            isModified: true,
        }));
        if (typeof global === 'string') {
            global = JSON.parse(global);
        }
        this.setState({
            pages: importedPages,
            hasModifiedGlobal: true,
            global: global || { ...initialData.global },
        });
    };

    get getPreviewHeader() {
        const { global } = this.state;
        if (!global) return null;
        const fontFamilies = buildFontString(global);
        const globalCSS = globalStyles(global);
        return (
            <>
                {fontFamilies && (
                    <link
                        href={`https://fonts.googleapis.com/css?family=${fontFamilies}&display=swap`}
                        rel="stylesheet"
                    />
                )}
                <link rel="stylesheet" href="/css/main.1.css" />
                <link rel="stylesheet" href="/css/builder-canvas.css" />
                {globalCSS ? <style>{globalCSS}</style> : null}
            </>
        );
    }

    render() {
        const {
            pages,
            _history,
            isSaving,
            addElType,
            isPublishing,
            editPageIndex,
            activeSidebar,
            settingsModal,
            currentEditItem,
            isLoadingBuilder,
            responsiveEditorMode,
        } = this.state;
        const editPage = this.getEditPage();
        const { data } = editPage;
        const { site, siteId, location, authUser } = this.props;
        if (!location.pathname.includes('page')) {
            const homepage = this.findHomePage();
            if (homepage?.id) {
                return (
                    <Redirect
                        to={`/builder/site/${siteId}/page/${homepage.id}`}
                    />
                );
            }
        }

        if (siteId && !authUser) {
            return <Redirect to="/login" />;
        }
        const pageTitle = data?.settings?.meta?.title;

        const hasNext = _history.cursor !== 0;
        const hasPrev = _history.records.length - 1 > _history.cursor;

        return (
            <>
                <Helmet>
                    <title>{pageTitle || 'Untitled'} | Dorik Builder</title>
                </Helmet>
                <ElementContextProvider value={this.elementContextMethods}>
                    <>
                        <BuilderHeader>
                            <HeaderNav isFirst={true}>
                                <NavUl>
                                    <li className="logo">
                                        <Link to="/">
                                            <Image
                                                width="70"
                                                alt="Dorik.io Single Page Website Builder"
                                                src="https://assets.dorik.com/5e443dca5619780040289e37/images/one-color-png_(1)_wdk58cni.png"
                                            />
                                        </Link>
                                    </li>
                                    <li
                                        onClick={() =>
                                            this.toggleActiveSidebar('PAGES')
                                        }
                                    >
                                        Page:{' '}
                                        <strong className="sidebar-active-item">
                                            {editPage.name}
                                        </strong>
                                    </li>
                                    <li
                                        className="_jr-page-settings"
                                        onClick={() =>
                                            this.showSettingsModal(
                                                'PAGE-SETTINGS'
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={['fal', 'tools']}
                                        />{' '}
                                        Page Settings
                                    </li>
                                </NavUl>
                            </HeaderNav>
                            <HeaderNav>
                                <NavUl className="device">
                                    <li
                                        className={
                                            responsiveEditorMode === 'desktop'
                                                ? 'active'
                                                : null
                                        }
                                        onClick={() =>
                                            this.handleResponsiveEditorMode(
                                                'desktop'
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'desktop-alt']}
                                        />
                                    </li>
                                    <li
                                        className={
                                            responsiveEditorMode === 'tablet'
                                                ? 'active'
                                                : null
                                        }
                                        onClick={() =>
                                            this.handleResponsiveEditorMode(
                                                'tablet'
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'tablet-alt']}
                                        />
                                    </li>
                                    <li
                                        className={
                                            responsiveEditorMode === 'mobile'
                                                ? 'active'
                                                : null
                                        }
                                        onClick={() =>
                                            this.handleResponsiveEditorMode(
                                                'mobile'
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'mobile-alt']}
                                        />
                                    </li>
                                </NavUl>
                            </HeaderNav>
                            <HeaderNav isLast={true}>
                                <NavUl>
                                    <li className="button-wrapper">
                                        <button
                                            disabled={!hasPrev}
                                            onClick={this.handleUndo}
                                        >
                                            <FontAwesomeIcon
                                                icon={['fal', 'undo']}
                                            />
                                        </button>
                                    </li>
                                    <li className="button-wrapper">
                                        <button
                                            disabled={!hasNext}
                                            onClick={this.handleRedo}
                                        >
                                            <FontAwesomeIcon
                                                icon={['fal', 'redo']}
                                            />
                                        </button>
                                    </li>

                                    {authUser ? (
                                        <>
                                            <li onClick={this.previewSite}>
                                                <FontAwesomeIcon
                                                    icon={['fal', 'eye']}
                                                />
                                            </li>
                                            <li
                                                disabled={isSaving}
                                                className="_jr-save-site"
                                                onClick={this.handleSaveData}
                                            >
                                                {this.renderSaveButton()}
                                            </li>
                                            <li
                                                disabled={isPublishing}
                                                className="active _jr-publish-site"
                                                onClick={
                                                    this.handleClickOnPublish
                                                }
                                            >
                                                Publish{' '}
                                                {isPublishing && (
                                                    <FontAwesomeIcon
                                                        spin
                                                        icon={[
                                                            'fal',
                                                            'spinner',
                                                        ]}
                                                    />
                                                )}
                                            </li>
                                            <li className="button-wrapper">
                                                <button
                                                    disabled={!site?.url}
                                                    onClick={() =>
                                                        site?.url &&
                                                        window.open(
                                                            `https://${site.url}`,
                                                            '_blank'
                                                        )
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={[
                                                            'fal',
                                                            'external-link',
                                                        ]}
                                                    />
                                                </button>
                                            </li>
                                        </>
                                    ) : (
                                        <li className="as-text">
                                            <Link
                                                to={{
                                                    pathname: '/signup',
                                                    state: { from: '/builder' },
                                                }}
                                            >
                                                Sign Up
                                            </Link>{' '}
                                            or{' '}
                                            <Link
                                                to={{
                                                    pathname: '/login',
                                                    state: { from: '/builder' },
                                                }}
                                            >
                                                Login
                                            </Link>{' '}
                                            to Publish
                                        </li>
                                    )}
                                    <li className="button-wrapper nav-dropdown">
                                        <Dropdown
                                            placement="bottomRight"
                                            content={
                                                <NavItemMore
                                                    importJSON={this.importJSON}
                                                    exportJSON={
                                                        this.handleExportJSON
                                                    }
                                                />
                                            }
                                        >
                                            <button className="nav-dropdown__btn">
                                                <FontAwesomeIcon
                                                    icon={['far', 'bars']}
                                                />
                                            </button>
                                        </Dropdown>
                                    </li>
                                </NavUl>
                            </HeaderNav>
                        </BuilderHeader>
                        <BuilderWrap>
                            <BuilderAside
                                pages={pages}
                                siteURL={site?.url}
                                activeSidebar={activeSidebar}
                                editPageIndex={editPageIndex}
                                onEditPage={this.setEditPage}
                                onRemovePage={this.removeEditPage}
                                onAddNewPage={this.addToEditPages}
                                duplicatePage={this.duplicatePage}
                                onSelectPage={this.handleSelectPage}
                                showSettingsModal={this.showSettingsModal}
                                toggleActiveSidebar={this.toggleActiveSidebar}
                            />
                            <BuilderBody>
                                {!isLoadingBuilder ? null : (
                                    <div
                                        style={{
                                            opacity: 0.5,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            size="3x"
                                            spin
                                            icon={['fal', 'spinner']}
                                        />
                                    </div>
                                )}
                                <Frame
                                    id="dorik-builder-iframe"
                                    style={{
                                        ...this.getFrameStyle,
                                        transition: 'opacity 0.3s',
                                        opacity: isLoadingBuilder ? 0 : 1,
                                    }}
                                    head={this.getPreviewHeader}
                                >
                                    {isLoadingBuilder ? null : (
                                        <FrameBindingContext>
                                            <RecursiveRender
                                                type="page"
                                                content={data?.content}
                                                onDragItem={this.onDragItem}
                                            />
                                        </FrameBindingContext>
                                    )}
                                </Frame>
                            </BuilderBody>

                            <EditorContextProvider value={this.stateToContext}>
                                <>
                                    {settingsModal === 'COMPONENT_SETTINGS' &&
                                        currentEditItem && (
                                            <EditorModal
                                                settings={elementSettings}
                                                close={this.hideSettingsModal}
                                                title={`${currentEditItem.name}`}
                                                editElType={
                                                    currentEditItem.type
                                                }
                                                onSaveSettings={
                                                    this.handleSaveSettings
                                                }
                                            />
                                        )}

                                    {settingsModal === 'GLOBAL-SETTINGS' && (
                                        <EditorModal
                                            settings={globalSettings}
                                            close={this.hideSettingsModal}
                                            title={`Global Styles`}
                                            onSaveSettings={
                                                this.handleGlobalChanges
                                            }
                                        />
                                    )}

                                    {settingsModal === 'PAGE-SETTINGS' && (
                                        <EditorModal
                                            settings={configIntegration}
                                            close={this.hideSettingsModal}
                                            title="Page &amp; Settings"
                                            onSaveSettings={
                                                this.handleSaveSettings
                                            }
                                        />
                                    )}

                                    {settingsModal === 'SITE-SETTINGS' && (
                                        <EditorModal
                                            settings={siteSettings}
                                            close={this.hideSettingsModal}
                                            title="Site Settings &amp; Integrations"
                                            onSaveSettings={
                                                this.handleGlobalChanges
                                            }
                                        />
                                    )}
                                </>
                            </EditorContextProvider>
                        </BuilderWrap>
                    </>

                    {addElType && (
                        <AddSectionModal
                            type={addElType}
                            onClose={this.hideAddModal}
                        />
                    )}

                    {settingsModal === 'PUBLISH-SITE' && (
                        <PublishSiteModal
                            site={site}
                            onSave={this.handlePublishSave}
                            onClose={this.hideSettingsModal}
                        />
                    )}

                    {settingsModal === 'SAVE-ELEMENT' && (
                        <SaveElementModal
                            _elType={currentEditItem._elType}
                            onClose={this.hideSettingsModal}
                            data={currentEditItem}
                        />
                    )}
                </ElementContextProvider>
            </>
        );
    }

    renderSaveButton() {
        const { isSaved } = this.props;
        const { isSaving } = this.state;
        if (isSaving) {
            return (
                <>
                    <FontAwesomeIcon spin icon={['fal', 'spinner']} /> Saving
                </>
            );
        } else if (isSaved) {
            return (
                <>
                    <FontAwesomeIcon icon={['fal', 'check']} /> Saved
                </>
            );
        } else {
            return (
                <>
                    <FontAwesomeIcon icon={['fal', 'save']} /> Save
                </>
            );
        }
    }
}

function mapStateToProps(state, props) {
    const {
        location,
        match: { params },
    } = props;
    const { siteId, pageId } = params;
    const urlParams = new URLSearchParams(location.search);
    const templateId = urlParams.get('t');
    let site;
    if (siteId) {
        site = getSite(state, siteId);
    } else if (templateId) {
        site = getTemplate(state, templateId);
    }

    const page = pageId && getPage(pageId)(state);
    const pages = site?.pages?.map((page) => ({
        ...page,
        ...getPage(page.id)(state),
    }));

    return {
        site,
        page,
        pages,
        pageId,
        siteId,
        templateId,
        authUser: getAuthUser(state),
        isSaved: getIsSavedSite(state),
    };
}

export default connect(mapStateToProps)(Builder);
